import { useGameStore } from '@/store/gameStore'
import { useAudioContext } from '@/contexts/AudioContext'
import { shopIcon1, shopIcon2, shopIcon3, shopIconAnchor } from '@/assets/ui/icons'
import type { BonusType } from '@/types'
import { fromMinor } from '@/utils/money'

interface ShopProps {
  isOpen: boolean
  onClose: () => void
}

// Стили
const titleStyle: React.CSSProperties = {
  fontFamily: "'Caesar Dressing', cursive",
  fontWeight: 400,
  fontSize: '37px',
  lineHeight: '91.8%',
  color: '#A52A1F',
}

const itemTitleStyle: React.CSSProperties = {
  fontFamily: "'Caesar Dressing', cursive",
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '91.8%',
  color: '#8C0000',
}

const itemDescStyle: React.CSSProperties = {
  fontFamily: 'Arial, sans-serif',
  fontWeight: 400,
  fontSize: '11px',
  lineHeight: '120%',
  color: '#630000',
}

const itemPriceStyle: React.CSSProperties = {
  fontFamily: 'Arial, sans-serif',
  fontWeight: 400,
  fontSize: '11px',
  lineHeight: '91.8%',
  color: '#6C0000',
}

const buyBtnStyle: React.CSSProperties = {
  fontFamily: 'Arial, sans-serif',
  fontWeight: 700,
  fontSize: '11px',
  lineHeight: '91.8%',
  color: '#F9E079',
}

const closeBtnStyle: React.CSSProperties = {
  fontFamily: "'Big Shoulders Display', sans-serif",
  fontWeight: 700,
  fontSize: '15px',
  lineHeight: '91.8%',
  color: '#F8DDCA',
  textTransform: 'uppercase' as const,
}

// Карточка товара
interface ShopItemProps {
  title: string
  description: string
  price: string
  icon: string
  onBuy: () => void
  disabled?: boolean
  height?: number
}

function ShopItem({ title, description, price, icon, onBuy, disabled, height = 86 }: ShopItemProps) {
  return (
    <div 
      className="flex items-center gap-2 px-2"
      style={{
        width: '272px',
        height: `${height}px`,
        background: '#F8DDCA',
        borderRadius: '5px',
      }}
    >
      {/* Иконка */}
      <div className="flex-shrink-0 flex items-center justify-center" style={{ width: '65px', height: '75px' }}>
        <img src={icon} alt="" className="max-w-full max-h-full object-contain" draggable={false} />
      </div>
      
      {/* Текст */}
      <div className="flex-1 flex flex-col gap-1">
        <span style={itemTitleStyle}>{title}</span>
        <span style={itemDescStyle}>{description}</span>
        <span style={itemPriceStyle}>{price}</span>
      </div>
      
      {/* Кнопка купить */}
      <button
        onClick={onBuy}
        disabled={disabled}
        className="flex-shrink-0 flex items-center justify-center active:scale-95 transition-transform disabled:opacity-50"
        style={{
          width: '59px',
          height: '22px',
          background: '#D83C48',
          boxShadow: 'inset 0px 2px 0px rgba(255, 119, 119, 0.25), inset 0px -3px 0px #AA2930',
          borderRadius: '2px',
        }}
      >
        <span style={buyBtnStyle}>BUY</span>
      </button>
    </div>
  )
}

export default function Shop({ isOpen, onClose }: ShopProps) {
  const { 
    currentStake, 
    hotRollsChanceBonus, 
    hasStability, 
    purchasedBonusIds,
    purchaseBonus,
    getHotRollsChance,
    status
  } = useGameStore()
  const { playSfx } = useAudioContext()

  if (!isOpen) return null
  
  // Стоимость бустеров от текущей ставки (WIN), не от баланса
  const cost10 = currentStake * 0.1
  const cost30 = currentStake * 0.3
  const cost50 = currentStake * 0.5
  const costStability = currentStake * 0.25

  // Проверяем, можно ли покупать (только во время игры)
  const canBuy = status === 'playing' || status === 'hot_rolls'

  // Hot rolls capped at 100% — disable hot-chance buys when at the limit
  const hotChanceMaxed = hotRollsChanceBonus >= 100
  const hotRollsBonusPurchased = purchasedBonusIds.some(id => id.startsWith('hot_rolls_'))
  const stabilityPurchased = purchasedBonusIds.includes('stability')
  
  const handleBuy = async (type: BonusType, cost: number) => {
    if (!canBuy) return

    // Map frontend bonus type to backend bonus_id
    const bonusIdMap: Record<BonusType, string> = {
      hot_chance_10: 'hot_rolls_10',
      hot_chance_30: 'hot_rolls_30',
      hot_chance_50: 'hot_rolls_50',
      stability: 'stability',
    }

    // Try WS first, fall back to local
    const { getGameWs } = await import('@/services/gameWebSocket')
    const ws = getGameWs()
    if (ws?.connected) {
      try {
        await ws.buyBonus(bonusIdMap[type])
        playSfx('click')
      } catch (err) {
        console.error('[Shop] WS buy_bonus failed:', err)
      }
    } else {
      const success = purchaseBonus(type, cost)
      if (success) playSfx('click')
    }
  }

  const handleClose = () => {
    playSfx('click')
    onClose()
  }

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleClose}
    >
      {/* Основной контейнер магазина */}
      <div 
        className="relative flex flex-col items-center"
        style={{
          width: '315px',
          background: '#282E44',
          border: '5px solid #060812',
          borderRadius: '18px',
          paddingTop: '85px', // Место для выступающего заголовка (+40px)
          paddingBottom: '60px', // Место для кнопки закрыть
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Заголовок Shop — выступает на 50% за верхний край */}
        <div 
          className="absolute flex flex-col items-center"
          style={{ top: '-28px' }} // 50% от высоты блока (55px / 2)
        >
          {/* Фон заголовка */}
          <div 
            style={{
              width: '199px',
              height: '48px',
              background: 'linear-gradient(180deg, #FAE045 0%, #F6C638 100%)',
              boxShadow: 'inset -9px 0px 0px rgba(201, 50, 21, 0.5), inset 9px 0px 0px rgba(201, 50, 21, 0.5), inset 0px 4px 0px #FEF99D, inset 0px -2px 0px #FED751',
            }}
            className="flex items-center justify-center"
          >
            <span style={titleStyle}>Shop</span>
          </div>
          {/* Полоска под заголовком */}
          <div 
            style={{
              width: '199px',
              height: '7px',
              background: 'linear-gradient(180deg, #CD7821 0%, #B1581C 100%)',
              boxShadow: 'inset -9px 0px 0px rgba(201, 50, 21, 0.5), inset 9px 0px 0px rgba(201, 50, 21, 0.5)',
            }}
          />
        </div>

        {/* Текущий шанс HOT ROLLS */}
        {canBuy && hotRollsChanceBonus > 0 && (
          <div 
            className="text-center mb-2"
            style={{ 
              marginTop: '-30px',
              fontFamily: "'Caesar Dressing', cursive",
              fontSize: '14px',
              color: '#F8DDCA'
            }}
          >
            HOT ROLLS chance: {getHotRollsChance()}%
          </div>
        )}
        
        {/* Карточки товаров */}
        <div className="flex flex-col gap-3" style={{ marginTop: hotRollsChanceBonus > 0 ? '0px' : '-38px' }}>
          <ShopItem
            title="+10% hot Rolls Chance"
            description={hotChanceMaxed ? "✓ HOT ROLLS chance is at 100%" : "Increases the chance of HOT ROLLS by 10%"}
            price={hotChanceMaxed ? "Maxed out" : `Cost: ${fromMinor(cost10)} (10% WIN)`}
            icon={shopIcon1}
            onBuy={() => handleBuy('hot_chance_10', cost10)}
            disabled={!canBuy || hotRollsBonusPurchased || hotChanceMaxed || cost10 >= currentStake}
          />

          <ShopItem
            title="+30% hot Rolls Chance"
            description={hotChanceMaxed ? "✓ HOT ROLLS chance is at 100%" : "Increases the chance of HOT ROLLS by 30%"}
            price={hotChanceMaxed ? "Maxed out" : `Cost: ${fromMinor(cost30)} (30% WIN)`}
            icon={shopIcon2}
            onBuy={() => handleBuy('hot_chance_30', cost30)}
            disabled={!canBuy || hotRollsBonusPurchased || hotChanceMaxed || cost30 >= currentStake}
          />

          <ShopItem
            title="+50% hot Rolls Chance"
            description={hotChanceMaxed ? "✓ HOT ROLLS chance is at 100%" : "Increases the chance of HOT ROLLS by 50%"}
            price={hotChanceMaxed ? "Maxed out" : `Cost: ${fromMinor(cost50)} (50% WIN)`}
            icon={shopIcon3}
            onBuy={() => handleBuy('hot_chance_50', cost50)}
            disabled={!canBuy || hotRollsBonusPurchased || hotChanceMaxed || cost50 >= currentStake}
          />
          
          <ShopItem
            title="Sustainability"
            description={hasStability ? "✓ Armed — saves you on next edge fall" : "Saves you once if your next roll lands on the edge. Consumed on use."}
            price={hasStability ? "Already armed" : `Cost: ${fromMinor(costStability)} 25% (WIN)`}
            icon={shopIconAnchor}
            onBuy={() => handleBuy('stability', costStability)}
            disabled={!canBuy || stabilityPurchased || hasStability || costStability >= currentStake}
            height={101}
          />
        </div>

        {/* Кнопка закрыть */}
        <button
          onClick={handleClose}
          className="absolute bottom-3 flex items-center justify-center active:scale-95 transition-transform"
          style={{
            width: '188px',
            height: '40px',
            background: '#DC3F48',
            boxShadow: 'inset 0px -3px 0px #AA2930, inset 0px 2px 0px rgba(255, 119, 119, 0.25)',
            borderRadius: '7px',
          }}
        >
          <span style={closeBtnStyle}>CLOSE</span>
        </button>
      </div>
    </div>
  )
}

